import React, { useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Check as CheckIcon,
  AccountTree as AccountTreeIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Label as LabelIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const STEPS = [
  'Basic Details',
  'Team Hierarchy',
  'Team Head',
  'Members',
  'Review',
];

const TeamCreationWizard = ({
  open,
  onClose,
  onSubmit,
  teams = [],
  users = [],
  accountId,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    parent_team: null,
    lead: null,
    members: [],
    tags: [],
  });

  // Filter users who can be team leads (managers and admins)
  const eligibleLeads = users.filter(user => ['MG', 'AD'].includes(user.role));
  
  // Filter potential members (exclude the selected lead, admins, and users already in teams)
  const potentialMembers = users.filter(user => 
    user.id !== formData.lead?.id && 
    user.role !== 'AD' && 
    !user.team_id
  );

  // Automatically add lead to members when selected
  React.useEffect(() => {
    if (formData.lead) {
      setFormData(prevData => {
        // If the lead is not already in members, add them
        if (!prevData.members.some(member => member.id === formData.lead.id)) {
          return { ...prevData, members: [formData.lead, ...prevData.members] };
        }
        return prevData;
      });
    } else {
      // If lead is removed, remove them from members too
      setFormData(prevData => {
        const newMembers = prevData.members.filter(member => member.id !== prevData.lead?.id);
        return { ...prevData, members: newMembers };
      });
    }
  }, [formData.lead]);

  const validateStep = () => {
    const newErrors = {};
    
    switch (activeStep) {
      case 0: // Basic Details
        if (!formData.name.trim()) {
          newErrors.name = 'Team name is required';
        } else if (teams.some(team => team.name.toLowerCase() === formData.name.toLowerCase())) {
          newErrors.name = 'Team name already exists';
        }
        break;
        
      case 1: // Team Hierarchy
        // Parent team validation is optional
        break;
        
      case 2: // Team Lead
        // Lead is optional but if selected, must be manager or admin
        if (formData.lead && !['MG', 'AD'].includes(formData.lead.role)) {
          newErrors.lead = 'Team lead must be a Manager or Admin';
        }
        break;
        
      case 3: // Members
        // Members are optional
        break;
        
      case 4: // Review
        // Final validation before submission
        if (!formData.name.trim()) {
          newErrors.name = 'Team name is required';
        }
        break;
        
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    if (validateStep()) {
      try {
        // Format data for API
        const payload = {
          name: formData.name,
          parent_team: formData.parent_team?.id || null,
          lead: formData.lead?.id || null,
          members: formData.members.map(member => member.id),
          tags: formData.tags.length > 0 ? formData.tags : null,
        };
        
        // Call the onSubmit function and wait for the result
        const result = await onSubmit(payload);
        
        // If result is true, the submission was successful
        if (result === true) {
          return true;
        } else if (result) {
          // Handle API validation errors
          const newErrors = {};
          Object.entries(result).forEach(([field, message]) => {
            newErrors[field] = Array.isArray(message) ? message[0] : message;
          });
          setErrors(newErrors);
          return false;
        }
      } catch (error) {
        console.error('Error submitting team:', error);
      }
    } else {
      console.error('Validation errors:', errors);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
    
    // Clear error for this field when changed
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };
  
  const handleTagDelete = (index) => {
    const newTags = [...formData.tags];
    newTags.splice(index, 1);
    setFormData({ ...formData, tags: newTags });
  };
  
  const handleAddTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
    }
  };
  
  const handleAddMember = (member) => {
    if (member && !formData.members.some(m => m.id === member.id)) {
      setFormData({ ...formData, members: [...formData.members, member] });
    }
  };
  
  const handleRemoveMember = (memberId) => {
    const newMembers = formData.members.filter(m => m.id !== memberId);
    setFormData({ ...formData, members: newMembers });
  };

  // Step content components
  const getStepContent = (step) => {
    switch (step) {
      case 0: // Basic Details
        return (
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Team Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              margin="normal"
              required
            />
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Team Tags (Optional)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {formData.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => handleTagDelete(index)}
                    color="primary"
                    size="small"
                    icon={<LabelIcon />}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', mt: 2 }}>
                <TextField
                  label="Add Tag"
                  size="small"
                  id="tag-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddTag(e.target.value);
                      e.target.value = '';
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    const input = document.getElementById('tag-input');
                    if (input.value) {
                      handleAddTag(input.value);
                      input.value = '';
                    }
                  }}
                  sx={{ ml: 1 }}
                >
                  Add
                </Button>
              </Box>
            </Box>
          </Box>
        );
        
      case 1: // Team Hierarchy
        return (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
              Select a parent team to establish hierarchy (optional).
            </Typography>
            
            <Autocomplete
              id="parent-team-select"
              options={teams}
              getOptionLabel={(option) => option.name}
              value={formData.parent_team}
              onChange={(event, newValue) => handleInputChange('parent_team', newValue)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Team"
                  variant="outlined"
                  helperText={errors.parent_team || "Leave empty if this is a top-level team"}
                  error={!!errors.parent_team}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccountTreeIcon sx={{ color: 'text.secondary', mr: 1 }} fontSize="small" />
                    <Typography variant="body2">{option.name}</Typography>
                  </Box>
                </Box>
              )}
            />
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              Note: Teams can only have one parent. Creating circular hierarchies is not allowed.
            </Typography>
          </Box>
        );
        
      case 2: // Team Lead
        return (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
              Assign a manager to lead this team (optional).
            </Typography>
            
            <Autocomplete
              id="team-lead-select"
              options={eligibleLeads}
              getOptionLabel={(option) => option.name}
              value={formData.lead}
              onChange={(event, newValue) => handleInputChange('lead', newValue)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Team Lead"
                  variant="outlined"
                  helperText={errors.lead || "Only Managers and Admins can be team leads"}
                  error={!!errors.lead}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ color: 'text.secondary', mr: 1 }} fontSize="small" />
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      ({option.role === 'MG' ? 'Manager' : 'Admin'})
                    </Typography>
                  </Box>
                </Box>
              )}
            />
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              A manager can only lead one team. The team lead will be automatically added as a team member.
            </Typography>
          </Box>
        );
        
      case 3: // Members
        return (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Add team members (optional). You can add or remove members later.
            </Typography>
            
            <Box sx={{ display: 'flex', mt: 2, mb: 2 }}>
              <Autocomplete
                id="add-member-select"
                options={potentialMembers}
                getOptionLabel={(option) => `${option.name} (${option.role === 'AG' ? 'Agent' : option.role === 'MG' ? 'Manager' : 'Admin'})`}
                sx={{ width: 300 }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Member" variant="outlined" size="small" />
                )}
                onChange={(event, value) => {
                  if (value) {
                    handleAddMember(value);
                  }
                }}
                value={null}
              />
              
            </Box>
            
            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', mt: 2 }}>
              <List dense>
                {formData.members.length > 0 ? (
                  formData.members.map((member) => {
                    const isLead = formData.lead && member.id === formData.lead.id;
                    return (
                      <ListItem
                        key={member.id}
                        secondaryAction={
                          isLead ? (
                            <Chip label="Team Lead" size="small" color="secondary" />
                          ) : (
                            <IconButton edge="end" aria-label="remove" onClick={() => handleRemoveMember(member.id)}>
                              <RemoveIcon />
                            </IconButton>
                          )
                        }
                        sx={{ backgroundColor: isLead ? 'action.hover' : 'inherit' }}
                      >
                        <ListItemAvatar>
                          <Avatar>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={member.name}
                          secondary={member.role === 'AG' ? 'Agent' : member.role === 'MG' ? 'Manager' : 'Admin'}
                        />
                      </ListItem>
                    );
                  })
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="No members added"
                      secondary="Use the controls above to add team members"
                      sx={{ color: 'text.secondary' }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Box>
        );
        
      case 4: // Review
        return (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Team Summary
            </Typography>
            
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Basic Details</Typography>
                <Typography variant="body2">Name: {formData.name}</Typography>
                {formData.tags.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">Tags:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {formData.tags.map((tag, index) => (
                        <Chip key={index} label={tag} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Team Hierarchy</Typography>
                <Typography variant="body2">
                  {formData.parent_team 
                    ? `Child of: ${formData.parent_team.name} (Level ${formData.parent_team.team_level})`
                    : 'Top-level team (no parent)'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Team Lead</Typography>
                <Typography variant="body2">
                  {formData.lead 
                    ? `${formData.lead.name} (${formData.lead.role === 'MG' ? 'Manager' : 'Admin'})`
                    : 'No team lead assigned'}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2">Team Members ({formData.members.length})</Typography>
                {formData.members.length > 0 ? (
                  <List dense disablePadding>
                    {formData.members.map((member) => (
                      <ListItem key={member.id} disablePadding>
                        <ListItemText
                          primary={member.name}
                          secondary={member.role === 'AG' ? 'Agent' : member.role === 'MG' ? 'Manager' : 'Admin'}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2">No members added</Typography>
                )}
              </Box>
            </Paper>
          </Box>
        );
        
      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GroupIcon sx={{ mr: 1 }} />
          Create New Team
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 2, mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {getStepContent(activeStep)}
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button 
          disabled={activeStep === 0} 
          onClick={handleBack} 
          sx={{ mr: 1 }}
        >
          Back
        </Button>
        {activeStep === STEPS.length - 1 ? (
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            startIcon={<CheckIcon />}
          >
            Create Team
          </Button>
        ) : (
          <Button 
            variant="contained" 
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TeamCreationWizard;
